import React, { useEffect, useState } from "react";
import {
  Box,
  SwipeableDrawer,
  Stack,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Button,
  Typography,
} from "@mui/material";

export const DEFAULT_STATUS = "in-progress";
export const DEFAULT_LOCATION = "";

export default function BookingFiltersMobileSheet({
  open,
  onClose,
  onOpen,
  // committed (current) values from parent:
  locationValue,
  dateOnValue,
  startDateValue,
  endDateValue,
  statusValue,
  // callbacks:
  onApply, // (draft) => void
  locations = [],
  statuses = [],
}) {
  // draft state: changes here don't affect parent until Apply
  const [location, setLocation] = useState(DEFAULT_LOCATION);
  const [dateOn, setDateOn] = useState("pickup");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [status, setStatus] = useState(DEFAULT_STATUS);

  // sync draft when the sheet opens
  useEffect(() => {
    if (open) {
      setLocation(locationValue ?? DEFAULT_LOCATION);
      setDateOn(dateOnValue ?? "pickup");
      setStartDate(startDateValue ?? "");
      setEndDate(endDateValue ?? "");
      setStatus(statusValue ?? DEFAULT_STATUS);
    }
  }, [
    open,
    locationValue,
    dateOnValue,
    startDateValue,
    endDateValue,
    statusValue,
  ]);

  const hasDraftFilters =
    location !== DEFAULT_LOCATION ||
    !!startDate ||
    !!endDate ||
    status !== DEFAULT_STATUS ||
    dateOn !== "pickup";

  const clearDraft = () => {
    setLocation(DEFAULT_LOCATION);
    setDateOn("pickup");
    setStartDate("");
    setEndDate("");
    setStatus(DEFAULT_STATUS);
  };

  const locationLabelId = "mobile-filter-location-label";
  const dateOnLabelId = "mobile-filter-dateon-label";
  const statusLabelId = "mobile-filter-status-label";

  return (
    <SwipeableDrawer
      anchor="bottom"
      open={open}
      onClose={onClose} // closes without committing
      onOpen={onOpen}
      disableDiscovery
      PaperProps={{
        sx: { borderTopLeftRadius: 12, borderTopRightRadius: 12, pb: 2 },
      }}>
      {/* Handle */}
      <Box sx={{ display: "flex", justifyContent: "center", py: 1 }}>
        <Box
          sx={{ width: 36, height: 4, borderRadius: 99, bgcolor: "divider" }}
        />
      </Box>

      <Box sx={{ px: 2, pb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
          Filters
        </Typography>

        <Stack spacing={1.75}>
          {/* Location */}
          <FormControl size="small" fullWidth>
            <InputLabel id={locationLabelId} shrink>
              Location
            </InputLabel>
            <Select
              labelId={locationLabelId}
              label="Location"
              value={location ?? DEFAULT_LOCATION}
              onChange={(e) => setLocation(e.target.value)}
              displayEmpty
              renderValue={(val) => (val ? val : <span>All Locations</span>)}>
              <MenuItem value={DEFAULT_LOCATION}>
                <em>All Locations</em>
              </MenuItem>
              {locations.map((loc) => (
                <MenuItem key={String(loc)} value={loc}>
                  {loc}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Date on */}
          <FormControl size="small" fullWidth>
            <InputLabel id={dateOnLabelId}>Date filter on</InputLabel>
            <Select
              labelId={dateOnLabelId}
              label="Date filter on"
              value={dateOn}
              onChange={(e) => setDateOn(e.target.value)}>
              <MenuItem value="pickup">Pickup date</MenuItem>
              <MenuItem value="order">Order date</MenuItem>
            </Select>
          </FormControl>

          {/* From/To */}
          <TextField
            label="From"
            type="date"
            size="small"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            fullWidth
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="To"
            type="date"
            size="small"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            fullWidth
            InputLabelProps={{ shrink: true }}
          />

          {/* Status */}
          <FormControl size="small" fullWidth>
            <InputLabel id={statusLabelId}>Status</InputLabel>
            <Select
              labelId={statusLabelId}
              label="Status"
              value={status}
              onChange={(e) => setStatus(e.target.value)}>
              <MenuItem value={DEFAULT_STATUS}>in-progress</MenuItem>
              {statuses
                .filter((s) => s !== DEFAULT_STATUS)
                .map((s) => (
                  <MenuItem key={String(s)} value={s}>
                    {s}
                  </MenuItem>
                ))}
              <MenuItem value="">
                <em>All Statuses</em>
              </MenuItem>
            </Select>
          </FormControl>
        </Stack>
      </Box>

      {/* Sticky footer */}
      <Box
        sx={{
          position: "sticky",
          bottom: 0,
          px: 2,
          pt: 1,
          pb: 1,
          display: "flex",
          alignItems: "center",
          gap: 1,
          bgcolor: "background.paper",
        }}>
        {hasDraftFilters && (
          <Button
            size="small"
            color="secondary"
            onClick={clearDraft}
            sx={{
              textTransform: "none",
              whiteSpace: "nowrap",
              minWidth: "unset",
            }}>
            Clear
          </Button>
        )}
        <Box sx={{ flex: 1 }} />
        <Button
          variant="contained"
          onClick={() => {
            onApply?.({ location, dateOn, startDate, endDate, status });
            onClose(); // close after applying
          }}
          sx={{ textTransform: "none", borderRadius: 2, px: 3, height: 40 }}>
          Apply
        </Button>
      </Box>
    </SwipeableDrawer>
  );
}
